import type { SupportedLocale } from "@chinese-vendor/i18n";
import { apiClient } from "./client";
import endpoint from "@/constants/endpoint";

export type StaffRole = "super_admin" | "admin" | "staff";

// ─── Backend raw types (snake_case) ─────────────────────────────────────────

interface BackendInvitationItem {
  id: string;
  recipient_name: string | null;
  recipient_email: string;
  invited_by_name: string;
  invited_by_role: string;
  sent_at: string;
  resent_count: number;
  status: "pending" | "accepted" | "expired";
}

interface BackendActivityLogEntry {
  action: string;
  performed_by: string;
  performed_at: string;
  note: string | null;
}

interface BackendInvitationDetail extends BackendInvitationItem {
  activity_log: BackendActivityLogEntry[];
}

interface BackendInvitationsListResponse {
  items: BackendInvitationItem[];
  total: number;
  new_count: number;
  resent_count: number;
  offset: number;
  limit: number;
}

// ─── Frontend types (camelCase) ───────────────────────────────────────────────

export interface Invitation {
  id: string;
  recipientName: string | null;
  recipientEmail: string;
  invitedByName: string;
  invitedByRole: StaffRole;
  sentAt: string;
  resentCount: number;
  status: "pending" | "accepted" | "expired";
}

export interface ActivityLogEntry {
  action: "sent" | "resent" | "accepted";
  actorName: string;
  sentAt: string;
  note: string | null;
}

export interface InvitationDetail extends Invitation {
  activityLog: ActivityLogEntry[];
}

export interface InvitationsListResponse {
  items: Invitation[];
  total: number;
  newCount: number;
  resentCount: number;
  page: number;
  pageSize: number;
}

export interface SendInvitationPayload {
  email: string;
  locale?: SupportedLocale;
}

// ─── Transformers ─────────────────────────────────────────────────────────────

function toInvitation(raw: BackendInvitationItem): Invitation {
  return {
    id: raw.id,
    recipientName: raw.recipient_name,
    recipientEmail: raw.recipient_email,
    invitedByName: raw.invited_by_name,
    invitedByRole: raw.invited_by_role as StaffRole,
    sentAt: raw.sent_at,
    resentCount: raw.resent_count,
    status: raw.status,
  };
}

function toActivityLogEntry(raw: BackendActivityLogEntry): ActivityLogEntry {
  return {
    action: raw.action as ActivityLogEntry["action"],
    actorName: raw.performed_by,
    sentAt: raw.performed_at,
    note: raw.note,
  };
}

function toInvitationDetail(raw: BackendInvitationDetail): InvitationDetail {
  return {
    ...toInvitation(raw),
    activityLog: raw.activity_log.map(toActivityLogEntry),
  };
}

// ─── API ──────────────────────────────────────────────────────────────────────

export const invitationsApi = {
  /** Staff: List invitations (with optional tab/search/page filter) */
  async list(params?: {
    tab?: "all" | "resent";
    search?: string;
    page?: number;
    pageSize?: number;
  }): Promise<InvitationsListResponse> {
    const pageSize = params?.pageSize ?? 20;
    const page = params?.page ?? 1;
    const offset = (page - 1) * pageSize;

    const qs = new URLSearchParams();
    qs.set("offset", String(offset));
    qs.set("limit", String(pageSize));
    if (params?.tab && params.tab !== "all") qs.set("tab", params.tab);
    if (params?.search) qs.set("search", params.search);

    const path = `${endpoint.invitations.list}?${qs.toString()}`;
    const res = await apiClient.get<BackendInvitationsListResponse>(path);
    const raw = res.data;

    return {
      items: raw.items.map(toInvitation),
      total: raw.total,
      newCount: raw.new_count,
      resentCount: raw.resent_count,
      page: Math.floor(raw.offset / raw.limit) + 1,
      pageSize: raw.limit,
    };
  },

  /** Staff: Get invitation detail */
  async getById(id: string): Promise<InvitationDetail> {
    const res = await apiClient.get<BackendInvitationDetail>(endpoint.invitations.detail(id));
    return toInvitationDetail(res.data);
  },

  /** Staff: Send a new invitation */
  async send(payload: SendInvitationPayload): Promise<void> {
    await apiClient.post<null>(endpoint.invitations.send, {
      email: payload.email,
      locale: payload.locale,
    });
  },

  /** Staff: Resend an existing invitation */
  async resend(id: string, options?: { locale?: SupportedLocale }): Promise<void> {
    await apiClient.post<null>(endpoint.invitations.resend(id), {
      locale: options?.locale,
    });
  },
};
